import { Group, Stack, createStyles } from '@mantine/core';
import { format } from 'date-fns';
import { useMemo, useState } from 'react';
import { IconLock, IconMenu2 } from '@tabler/icons-react';
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis, restrictToWindowEdges } from '@dnd-kit/modifiers';

import dt from '~/domains/contest/domains/manage/domains/common/testing.const';
import { Text } from '~/domains/design-system/Text';

import type { SegmentsFieldItemType, SegmentsFieldType } from '../types';
import { pluralizeSlateLabel } from '../../../../utils/pluralizeSlateLabel';

const useEditStyles = createStyles((theme) => ({
  description: {
    display: 'block',
    margin: `${theme.spacing.xs}px 0`,
  },
  item: {
    borderRadius: theme.radius.md,
    padding: `${theme.spacing.md / 2}px ${theme.spacing.md}px`,
  },
  segmentWrapper: {
    '&.isDragging': {
      opacity: 0.2,
    },
  },
  segment: {
    background: theme.colors.gray[7],
    color: theme.white,
    height: 44,
    position: 'relative',
    userSelect: 'none',

    '&.isDraggable': {
      cursor: 'grab',
    },
    '&:not(.isDraggable)': {
      cursor: 'not-allowed',
    },
  },
  dragIcon: {
    position: 'absolute',
    left: theme.spacing.xs,
  },
  slate: {
    border: `1px solid ${theme.colors.gray[3]}`,
    transition: 'margin-top 100ms ease',
    userSelect: 'none',

    '&.isOver': {
      marginTop: 44 + theme.spacing.xs,
    },
  },
  slateLabel: {
    flexShrink: 1,
  },
  slateDate: {
    flexShrink: 0,
  },
}));

function SegmentEditSlate({
  slate,
  isDropDisabled,
}: {
  slate: SegmentsFieldItemType;
  isDropDisabled: boolean;
}) {
  const { classes, cx } = useEditStyles();
  const { isOver, setNodeRef } = useDroppable({
    id: slate.key,
    disabled: isDropDisabled,
  });

  return (
    <div ref={setNodeRef}>
      <Group
        key={slate.key}
        position="apart"
        className={cx(classes.item, classes.slate, { isOver })}
        noWrap
        data-test-id={dt.segments.edit.slate}
      >
        <Text variant="subhead-medium" className={classes.slateLabel}>
          {slate.label}
        </Text>
        <Text variant="body-large" className={classes.slateDate}>
          {format(slate.date, 'M/dd')}
        </Text>
      </Group>
    </div>
  );
}

function SegmentEditSegmentDumb({ index, isDraggable }: { index: number; isDraggable: boolean }) {
  const { classes, cx } = useEditStyles();

  return (
    <Group
      className={cx(classes.item, classes.segment, { isDraggable })}
      position="center"
      spacing="xs"
      data-test-id={dt.segments.edit.segment}
    >
      <Text variant="headline-medium">Segment {index + 1}</Text>
      {!isDraggable ? <IconLock stroke={1.5} /> : <IconMenu2 className={classes.dragIcon} />}
    </Group>
  );
}

function SegmentEditSegment({ index, isDraggable }: { index: number; isDraggable: boolean }) {
  const { classes, cx } = useEditStyles();
  const {
    //
    attributes,
    listeners,
    setNodeRef,
    isDragging,
  } = useDraggable({
    id: index,
    disabled: !isDraggable,
  });

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      className={cx(classes.segmentWrapper, { isDragging })}
    >
      <SegmentEditSegmentDumb index={index} isDraggable={isDraggable} />
    </div>
  );
}

export function SegmentsEdit({
  slates,
  rules,
  value,
  onChange,
  slateLabel = 'tournaments',
}: {
  slates: SegmentsFieldItemType[];
  rules: SegmentsFieldType['rules'];
  value: string[][];
  slateLabel?: string;
  onChange: (newValueOrCb: string[][] | ((prev: string[][]) => string[][])) => void;
}) {
  const { classes } = useEditStyles();
  const [activeIndex, setActiveIndex] = useState<number>(null);

  const mouseSensor = useSensor(MouseSensor);
  const touchSensor = useSensor(TouchSensor);
  const sensors = useSensors(mouseSensor, touchSensor);

  const slatesMap = useMemo(
    () =>
      slates.reduce(
        (acc, slate) => {
          acc[slate.key] = slate;

          return acc;
        },
        {} as Record<string, SegmentsFieldItemType>
      ),
    [slates]
  );

  const activeItemSlateIds = value[activeIndex];

  const handleDragStart = (event) => {
    setActiveIndex(event.active.id);
  };
  const handleDragEnd = (event) => {
    const overId = event.over?.id;
    if (overId) {
      const prevItemSlateIds = value[activeIndex - 1];
      const possibleSlateIds = [...prevItemSlateIds, ...activeItemSlateIds];
      const overSlateIndex = possibleSlateIds.indexOf(overId);
      if (!overSlateIndex) {
        return;
      }

      onChange((segments) => {
        const newSegments = [...segments];
        newSegments[activeIndex - 1] = possibleSlateIds.slice(0, overSlateIndex);
        newSegments[activeIndex] = possibleSlateIds.slice(overSlateIndex);

        return newSegments;
      });
    }
    setActiveIndex(null);
  };

  const droppableSlatesIds = useMemo(() => {
    if (!activeItemSlateIds) {
      return [];
    }

    const prevItemSlateIds = value[activeIndex - 1];

    return [
      ...(prevItemSlateIds?.slice(rules.minSlateGroupsPerSegment) ?? []),
      ...(activeItemSlateIds?.slice(1, -rules.minSlateGroupsPerSegment + 1) ?? []),
    ];
  }, [activeItemSlateIds, value, activeIndex, rules.minSlateGroupsPerSegment]);

  return (
    <DndContext
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToVerticalAxis]}
      sensors={sensors}
    >
      <div>
        <Text variant="body-medium" className={classes.description}>
          To customize your segments, drag the segment rows up or down within the tournament list.
          You can not move segment 1 as that is the start date of this contest. You must have at
          least {rules.minSlateGroupsPerSegment}{' '}
          {pluralizeSlateLabel(rules.minSlateGroupsPerSegment, slateLabel).toLowerCase()} per
          segment.
        </Text>
        <Stack spacing="xs">
          {value.map((slateIds, index) => {
            const prevSegmentSlateIds = value[index - 1];
            const isDraggable = !prevSegmentSlateIds
              ? false
              : slateIds.length > rules.minSlateGroupsPerSegment ||
                prevSegmentSlateIds.length > rules.minSlateGroupsPerSegment;
            return (
              <>
                {/* eslint-disable-next-line react/no-array-index-key */}
                <SegmentEditSegment key={index} index={index} isDraggable={isDraggable} />
                {slateIds.map((slateId) => {
                  const slate = slatesMap[slateId];
                  if (!slate) {
                    return;
                  }

                  return (
                    <SegmentEditSlate
                      key={slateId}
                      slate={slate}
                      isDropDisabled={!droppableSlatesIds.includes(slateId)}
                    />
                  );
                })}
              </>
            );
          })}
        </Stack>
      </div>
      <DragOverlay modifiers={[restrictToWindowEdges]}>
        {activeIndex ? <SegmentEditSegmentDumb index={activeIndex} isDraggable /> : null}
      </DragOverlay>
    </DndContext>
  );
}
